import { useEffect, useState } from 'react';

import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { PlanUrlSet } from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { itemByLanguage } from '@/utils/language';
import { getClickableLink } from '@/utils/url';

import { BrightcovePlayer } from '@/components/BrightcovePlayer';

import {
  getBrightcoveEmbedUrl,
  isBrightcoveTaskLinkUrl,
  resolveBrightcoveEmbedUrl,
} from './brightcoveVideoUrl';
import { trackPlanTaskBounce } from './trackPlanTaskBounce';
import {
  getYouTubeEmbedUrl,
  subscribeYouTubeEmbedPostMessages,
} from './youtubeVideoUrl';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

const nativeUpdatedLinkFunctionalityEnabled =
  (window as { nativeUpdatedLinkFeatureFlag?: boolean })
    .nativeUpdatedLinkFeatureFlag ?? false;

interface PlanItemStepLinkProps {
  link: PlanUrlSet;
  language?: string;
  planId: string;
  stepId?: string;
  stepName?: string;
}

interface InlineVideoEmbedProps {
  embedUrl: string;
  isYouTube: boolean;
  linkLabel: string;
  stepName?: string;
}

const InlineVideoEmbed = ({
  embedUrl,
  isYouTube,
  linkLabel,
  stepName,
}: InlineVideoEmbedProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-4 pb-2.5">
      <div className="flex flex-col">
        <Typography variant="overline3">{t('Generic.link')}</Typography>
        <Typography
          className="text-secondary-base wrap-break-word underline"
          variant="body1"
        >
          {linkLabel}
        </Typography>
      </div>
      <div className="dark:border-outline-3 aspect-video w-full border border-solid border-[#51504c] bg-black">
        <iframe
          allow="fullscreen; encrypted-media; picture-in-picture"
          className="content-iframe size-full border-0"
          onLoad={
            isYouTube
              ? event => subscribeYouTubeEmbedPostMessages(event.currentTarget)
              : undefined
          }
          src={embedUrl}
          title={stepName ?? linkLabel}
        />
      </div>
    </div>
  );
};

interface BrightcoveTaskLinkProps {
  url: string;
  linkLabel: string;
  stepName?: string;
  onExternalLinkClick: () => void;
}

/** Inline Brightcove embed for task links — direct player URL or async fetch. */
const BrightcoveTaskLink = ({
  url,
  linkLabel,
  stepName,
  onExternalLinkClick,
}: BrightcoveTaskLinkProps) => {
  const { t } = useTranslation();
  const [resolvedEmbedUrl, setResolvedEmbedUrl] = useState<string>();
  const [brightcoveResolveFailed, setBrightcoveResolveFailed] = useState(false);

  // Direct players.brightcove.net links only — portal (video.cfahome.com) and
  // content /brightcove/ wrappers return undefined and need async HTML fetch.
  const directPlayerEmbedUrl = getBrightcoveEmbedUrl(url);
  const embedUrl = resolvedEmbedUrl ?? directPlayerEmbedUrl;

  useEffect(() => {
    if (directPlayerEmbedUrl) {
      return;
    }

    // Portal and /brightcove/ wrapper URLs: fetch page HTML, extract player iframe
    // src (same flow as DocumentPreview). Ignore late results after unmount;
    // parent key={url} remounts on URL change.
    let cancelled = false;

    resolveBrightcoveEmbedUrl(url)
      .then(resolvedUrl => {
        if (cancelled) {
          return;
        }

        if (resolvedUrl) {
          setResolvedEmbedUrl(resolvedUrl);
          return;
        }

        setBrightcoveResolveFailed(true);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setBrightcoveResolveFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [directPlayerEmbedUrl, url]);

  const linkContent = (
    <>
      <Typography variant="body1">{t('Generic.link')}</Typography>
      <Typography
        className="text-secondary-base hover:text-secondary-hover wrap-break-word underline hover:cursor-pointer hover:font-normal hover:underline"
        variant="body1"
      >
        {linkLabel}
      </Typography>
    </>
  );

  if (brightcoveResolveFailed) {
    const externalLink =
      window.native.isNative && nativeUpdatedLinkFunctionalityEnabled ? (
        <button
          type="button"
          className="text-left"
          onClick={() => {
            onExternalLinkClick();
            sendNativeMessage({
              type: 'planLinkClicked',
              payload: url,
            });
          }}
        >
          {linkContent}
        </button>
      ) : (
        <a
          className="text-secondary-base! hover:text-secondary-hover! hover:[&>p]:text-secondary-hover! [&>p]:text-secondary-base!"
          href={url}
          onClick={onExternalLinkClick}
          target="_blank"
          rel="noreferrer"
        >
          {linkContent}
        </a>
      );

    return (
      <div className="flex w-full flex-col gap-4 pb-2.5">
        <div className="text-feedback-error-base flex items-center">
          <IconAlertTriangleFilled size={16} />
          <Typography
            className="text-feedback-error-base! ml-2!"
            variant="body1"
          >
            {t('LoadingResourceError.errorParagraph')}
          </Typography>
        </div>
        {externalLink}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 pb-2.5">
      <div className="flex flex-col">
        <Typography variant="overline3">{t('Generic.link')}</Typography>
        <Typography
          className="text-secondary-base wrap-break-word underline"
          variant="body1"
        >
          {linkLabel}
        </Typography>
      </div>
      {embedUrl && (
        <BrightcovePlayer
          className="dark:border-outline-3 content-iframe aspect-video w-full border border-solid border-[#51504c] bg-black"
          src={embedUrl}
          title={stepName ?? linkLabel}
        />
      )}
    </div>
  );
};

const PlanItemStepLink: React.FC<PlanItemStepLinkProps> = ({
  link,
  language,
  planId,
  stepId,
  stepName,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();

  const linkLabel =
    itemByLanguage(link.urlSet, undefined, language) ??
    getClickableLink(itemByLanguage(link.urlSet, '', language));
  const url = getClickableLink(itemByLanguage(link.urlSet, '', language));
  const youtubeEmbedUrl = getYouTubeEmbedUrl(url);

  const content = (
    <>
      <Typography variant="body1">{t('Generic.link')}</Typography>
      <Typography
        className="text-secondary-base hover:text-secondary-hover wrap-break-word underline hover:cursor-pointer hover:font-normal hover:underline"
        variant="body1"
      >
        {linkLabel}
      </Typography>
    </>
  );

  const trackLinkClick = () => {
    trackPlanTaskBounce(track, {
      url,
      planId,
      stepId,
      stepName,
    });
  };

  if (youtubeEmbedUrl) {
    return (
      <InlineVideoEmbed
        embedUrl={youtubeEmbedUrl}
        isYouTube
        linkLabel={linkLabel}
        stepName={stepName}
      />
    );
  }

  if (isBrightcoveTaskLinkUrl(url)) {
    return (
      <BrightcoveTaskLink
        key={url}
        url={url}
        linkLabel={linkLabel}
        stepName={stepName}
        onExternalLinkClick={trackLinkClick}
      />
    );
  }

  if (window.native.isNative && nativeUpdatedLinkFunctionalityEnabled) {
    return (
      <button
        type="button"
        className="pb-2.5! text-left"
        onClick={() => {
          trackLinkClick();
          sendNativeMessage({
            type: 'planLinkClicked',
            payload: url,
          });
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      className="text-secondary-base! hover:text-secondary-hover! hover:[&>p]:text-secondary-hover! [&>p]:text-secondary-base! pb-2.5"
      href={url}
      onClick={trackLinkClick}
      target="_blank"
      rel="noreferrer"
    >
      {content}
    </a>
  );
};

export default PlanItemStepLink;
